import { logError } from 'utils/log';
import { API, destroy, get, patch, put } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { createReducer } from 'utils/redux';

import { danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import {
  Action,
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentApprovalStatus,
  DeliverableCaption,
  DeliverableClientApproval,
  DeliverableLook,
  ThunkAction
} from 'types';

applyTranslations(require('./deliverable-content-approval.i18n.json'));

type DeliverableContentApproval = {
  clientApprovalData: Record<number, DeliverableClientApproval | {}>,
  looks: Record<number, DeliverableLook>,
  lastCreatedLook?: DeliverableLook
}

export type DeliverableEditNoteResponse = {
  id: number,
  date: string,
  text: string
}

const initialState: DeliverableContentApproval = {
  clientApprovalData: {},
  looks: {},
  lastCreatedLook: undefined
};

const RESET_CLIENT_APPROVAL = 'deliverableContentApproval/RESET_CLIENT_APPROVAL';
const RESET_LAST_CREATED_LOOK = 'deliverableContentApproval/RESET_LAST_CREATED_LOOK';
const LOAD_CLIENT_APPROVAL_DATA = 'deliverableContentApproval/LOAD_CLIENT_APPROVAL_DATA';
const EDIT_CLIENT_NOTE = 'deliverableContentApproval/EDIT_CLIENT_NOTE';
const DELETE_CLIENT_NOTE = 'deliverableContentApproval/DELETE_CLIENT_NOTE';
const LOAD_LOOKS = 'deliverableContentApproval/LOAD_LOOKS';
const LOAD_DELIVERABLE = 'deliverableContentApproval/LOAD_DELIVERABLE';
const LOADING = 'deliverableContentApproval/LOADING';
const RESET = 'deliverableContentApproval/RESET';

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function resetOfferDeliverables(): Action {
  return {
    type: RESET
  };
}

export function resetClientApprovalDeliverable(deliverableId: number): Action {
  return {
    type: RESET_CLIENT_APPROVAL,
    payload: {
      deliverableId
    }
  };
}

export function resetLastCreatedLook(): Action {
  return {
    type: RESET_LAST_CREATED_LOOK
  };
}

export function loadClientApprovalData(deliverableId: number, deliverableClientApprovalData: DeliverableClientApproval): Action {
  return {
    type: LOAD_CLIENT_APPROVAL_DATA,
    payload: {
      deliverableId,
      deliverableClientApprovalData
    }
  };
}

export function updateClientNote(deliverableId: number, updatedNote: DeliverableEditNoteResponse) {
  return {
    type: EDIT_CLIENT_NOTE,
    payload: {
      deliverableId,
      updatedNote
    }
  };
}

export function deleteClientNote(deliverableId: number, noteId: number) {
  return {
    type: DELETE_CLIENT_NOTE,
    payload: {
      deliverableId,
      noteId
    }
  };
}

export function loadLooks(campaignId: number, looks: Array<DeliverableLook>, lastCreatedLookName?: string): Action {
  return {
    type: LOAD_LOOKS,
    payload: {
      campaignId,
      looks,
      lastCreatedLookName
    }
  };
}

export const getPreviouslySavedDeliverable = (
  offerId: number,
  deliverableId: number
): ThunkAction => {
  const url = API.END_POINTS.DELIVERABLE.getOfferDeliverable(offerId, deliverableId);

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    try {
      const response = (await get(url)) as { data: Deliverable };

      return response.data;
    } catch (error) {
      dispatch(setLoadingStatus(false));
      dispatch(danger(__t('deliverable_content_approval_reducer.get_previously_saved_deliverable_failed')));
      throw error;
    } finally {
      dispatch(setLoadingStatus(false));
    }
  };
};

export function updateContentApprovalData(
  offerId: number,
  deliverableId: number,
  addAttachments: Array<DeliverableAttachment>,
  deleteAttachments: Array<number>,
  caption: DeliverableCaption,
  note: any
) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.saveContentApprovalData(offerId, deliverableId);

    dispatch(showLoading(url));

    try {
      const data: {
        addAttachments: Array<DeliverableAttachment>,
        deleteAttachmentsIds: Array<number>,
        caption: string,
        note?: any
      } = {
        addAttachments,
        deleteAttachmentsIds: deleteAttachments,
        caption: caption.text
      };

      if (note?.text) {
        data.note = note;
      }

      const response = await put(url, {
        data
      });

      if (addAttachments?.length || deleteAttachments?.length || caption?.text) {
        dispatch(success(__t('deliverable_content_approval_reducer.update_success')));
      }

      return response.data;
    } catch (error) {
      logError(error);
      dispatch(danger(__t('deliverable_content_approval_reducer.update_failed')));

      return null;
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function markApprovalNoteAsRead(offerId: number, deliverableId: number, ids: Array<number>, readerType: 'brand' | 'influencer') {
  return async () => {
    try {
      const url = API.END_POINTS.DELIVERABLE.markApprovalNoteAsRead(offerId, deliverableId);

      const data = {
        readerType,
        ids
      };

      await put(url, { data });
    } catch (error) {
      logError(error);
    }
  };
}

export function updateApprovalStatus(
  offerId: number,
  deliverableId: number,
  attachments: Array<{ id: number, status: DeliverableAttachmentApprovalStatus}>,
  caption?: DeliverableAttachmentApprovalStatus
) {
  return async () => {
    try {
      const url = API.END_POINTS.DELIVERABLE.setApprovalStatus(offerId, deliverableId);

      const data = {
        attachments,
        caption
      };

      const results = await put(url, { data });

      return results?.data?.contentApprovalTimeline;
    } catch (error) {
      logError(error);
      return undefined;
    }
  };
}

export function getClientApprovalMedia(offerId: number, deliverableId: number) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.getClientApprovalData(offerId, deliverableId);

    dispatch(showLoading(url));

    try {
      const response = await get(url);

      dispatch(loadClientApprovalData(deliverableId, response?.data || {}));
    } catch (error) {
      dispatch(danger(__t('deliverable_content_approval_reducer.client_approval_receive_failed')));

      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function getLooks(campaignId: number) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.getLooksForClientApproval(campaignId);

    dispatch(showLoading(url));

    try {
      const response = await get(url);

      dispatch(loadLooks(campaignId, response?.data?.looks || []));
    } catch (error) {
      dispatch(danger(__t('deliverable_content_approval_reducer.client_approval_looks_receive_failed')));

      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function updateLooks(campaignId: number, looks: Array<DeliverableLook>) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.updateLooksForClientApproval(campaignId);

    dispatch(showLoading(url));

    const lastCreatedLookName = looks
      ?.filter((look) => look.id < 1)
      ?.slice(-1)
      ?.[0]
      ?.title;

    try {
      const response = await put(url, {
        data: {
          looks: looks.map(look => {
            if (look.id < 1) {
              return { ...look, id: undefined };
            }

            return look;
          })
        }
      });

      dispatch(success(__t('deliverable_content_approval_reducer.client_approval_looks_update_success')));
      return dispatch(loadLooks(campaignId, response?.data?.looks || [], lastCreatedLookName));
    } catch (error) {
      dispatch(danger(__t('deliverable_content_approval_reducer.client_approval_looks_update_failed')));

      logError(error);

      return null;
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function updateClientApproval(offerId: number, deliverableId: number, data, action: 'unpublish' | 'publish' = 'publish') {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.updateClientApprovalData(offerId, deliverableId);

    dispatch(showLoading(url));

    try {
      const response = await put(url, {
        data
      });

      dispatch(success(__t(`deliverable_content_approval_reducer.client_approval_update_${action}_success`)));
      dispatch(loadClientApprovalData(deliverableId, response?.data || {}));
    } catch (error) {
      dispatch(danger(__t(`deliverable_content_approval_reducer.client_approval_update__${action}_failed`)));

      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function editClientNote(deliverableId: number, noteId: number, noteValue: string) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.editApprovalNote(noteId);
    dispatch(showLoading(url));

    try {
      const requestMethod = noteValue ? patch : destroy;
      const response = await requestMethod(url, {
        data: { text: noteValue }
      }, { suppressGlobalNotification: true });
      const updatedNote: DeliverableEditNoteResponse = response?.data || {};

      if (noteValue) {
        dispatch(updateClientNote(deliverableId, updatedNote));
      } else {
        dispatch(deleteClientNote(deliverableId, noteId));
      }
    } catch (error) {
      dispatch(danger(__t('deliverable_content_approval_reducer.note_update_failed')));
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export default createReducer<DeliverableContentApproval, any>('deliverableContentApproval', initialState, {
  [RESET_CLIENT_APPROVAL]: (state, { deliverableId }) => ({
    ...state,
    clientApprovalData: {
      ...state.clientApprovalData,
      [deliverableId]: {}
    }
  }),
  [LOAD_CLIENT_APPROVAL_DATA]: (state, { deliverableId, deliverableClientApprovalData }) => ({
    ...state,
    clientApprovalData: {
      ...state.clientApprovalData,
      [deliverableId]: {
        ...(state.clientApprovalData[deliverableId] || {}),
        ...deliverableClientApprovalData
      }
    }
  }),
  [EDIT_CLIENT_NOTE]: (state, { deliverableId, updatedNote }) => ({
    ...state,
    clientApprovalData: {
      ...state.clientApprovalData,
      [deliverableId]: {
        ...(state.clientApprovalData[deliverableId] || {}),
        // @ts-ignore because of possibility state.clientApprovalData[deliverableId] be an empty object
        notes: (state.clientApprovalData[deliverableId]?.notes || [])?.map(note => {
          if (note.id === updatedNote.id) {
            return {
              ...note,
              ...updatedNote
            };
          }

          return note;
        })
      }
    }
  }),
  [DELETE_CLIENT_NOTE]: (state, { deliverableId, noteId }) => ({
    ...state,
    clientApprovalData: {
      ...state.clientApprovalData,
      [deliverableId]: {
        ...(state.clientApprovalData[deliverableId] || {}),
        // @ts-ignore because of possibility state.clientApprovalData[deliverableId] be an empty object
        notes: (state.clientApprovalData[deliverableId]?.notes || [])?.map(note => {
          if (note.id === noteId) {
            return {
              ...note,
              isDeleted: true
            };
          }

          return note;
        })
      }
    }
  }),
  [LOAD_LOOKS]: (state, { campaignId, looks, lastCreatedLookName }) => ({
    ...state,
    looks: {
      ...state.looks,
      [campaignId]: looks
    },
    lastCreatedLook: (lastCreatedLookName) ? looks.find(look => look.title === lastCreatedLookName) : undefined
  }),
  [LOAD_DELIVERABLE]: (state, { deliverable }) => ({
    ...state,
    deliverable
  }),
  [RESET_LAST_CREATED_LOOK]: (state) => ({
    ...state,
    lastCreatedLook: undefined
  })
});
